<template lang="pug">
  v-popover(
    :disabled='noActions'
    :placement='actionsPlacement'
    :delay='{show: 100, hide: 600}'
    trigger='hover click'
    popoverClass='krea-actions-popover'
    :container='false'
    @update:open='updateOpen'
  )
    .width-100(
      :label='label'
      :isOptional='isOptional'
      :errorMessage='errorMessage'
      :fieldHeight='fieldHeight'
      :valueLength='valueLength'
      :maxCharacters='maxCharacters'
      :showError='showError'
      :disabled='disabled'
    )
      .column.width-100
        .row.space-between.middle(v-if='label')
          label.text-normal {{ `${ label } ${ showRequiredMark ? `${$t('inputs.requiredMark')}` : ''}` }}
        .relative.width-100
          textarea.input-base-krea.wrap.width-100(
            v-if='!value || edition'
            ref='kreaTextarea'
            tabIndex='0'
            :name='name'
            :value='value'
            :maxLength='maxCharacters'
            :placeholder='placeholder'
            @input='updateInput'
            @keydown.enter='submit'
            @focus.prevent='setEdition(true)'
            @blur.prevent='setEdition(false)'
            :type='inputType'
            :disabled='disabled'
            :style='textareaStyles'
            :class='textAreaClasses'
          )
          h2.word-break.text-value(
            v-else
            @click='setEdition(true)'
            :class='{[size]: size, "program-title": programTitle}'
          ) {{ value }}
          span.absolute.max-characters.text-small.gray(v-if='maxCharacters && edition')
            | {{ `${value ? value.length : 0}/${maxCharacters} ${$t('krea.kreaTextArea.characters')}` }}
        .row.m-top-1(v-if='showError')
          simple-svg.error-icon.red-icon.m4-right-2.red-stroke-icon(:filepath='errorIcon' :width='"16px"' :height='"16px"')
          span.text-small.red {{ errorMessage }}
    template(slot="popover")
      actions(
        :edit='edit'
        :comments='comments'
        :pdf='pdf'
        :link='link'
        :info='info'
        :open='actionsOpen'
        @edit='setEdition(!edition)'
        :placement='actionsPlacement.split("-")[1]'
      )
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway'
import errorIcon from '@/assets/ic_stop.svg'
import inputValidation from '@/mixins/inputsValidationMixin'
import Actions from './Actions/Actions'
import kreaActions from '@/mixins/kreaActions'

export default {
  mixins: [ inputValidation, clickaway, kreaActions ],
  components: { Actions },
  props: {
    height: { type: String },
    size: { type: String, default: 'normal' },
    placeholderSize: { type: String },
    name: { type: String, required: true },
    label: { type: String },
    value: { type: String },
    maxCharacters: { type: Number },
    placeholder: { type: String },
    noActions: { type: Boolean },
    isOptional: { type: Boolean },
    updateMethod: { type: Function },
    errorMessage: { type: String },
    fieldHeight: { type: String, default: 'auto' },
    inputType: { type: String, default: 'text' },
    disabled: { type: Boolean },
    fontSize: { type: String },
    showRequiredMark: { type: Boolean },
    programTitle: { type: Boolean }
  },
  data() {
    return {
      errorIcon,
      edition: false,
      actionsOpen: false
    }
  },
  computed: {
    valueLength() {
      return this.value ? this.value.length : 0
    },
    textareaStyles() {
      return {
        '--textarea-height': this.height,
        '--font-size': this.fontSize
      }
    },
    textAreaClasses() {
      return {
        filled: !this.edition && this.value,
        [this.size]: this.size,
        [`placeholder-${this.placeholderSize || this.size}`]: this.placeholderSize,
        customSize: this.fontSize
      }
    }
  },
  methods: {
    updateOpen(value) {
      this.actionsOpen = value
    },
    setEdition(value) {
      if (this.disabled) return
      this.edition = value
      if (!this.edition) this.verifyValidation()
      else {
        if (this.$refs.kreaTextArea) this.$refs.kreaTextArea.focus()
      }
    },
    updateInput(event) {
      this.updateMethod(event.target.value, this.name)
    },
    submit(event) {
      this.setEdition(false)
      this.$emit('submit', event.target.value.trim())
      event.target.blur()
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors.scss';
@import 'src/scss/variables/sizes.scss';
@import 'src/scss/common/texts.scss';

.max-characters{
  bottom: 5px;
  right: 5px;
}

.input-base-krea{
  border-radius: 6px;
  border: none;
  padding: 5px 10px;
  min-height: 40px;
  background-color: $light-gray;
  color: $black;
  &.filled{
    background-color: transparent;
    padding: 0;
    color: $black;
  }
  &.wrap{
    resize: none;
    padding: 5px 10px;
    min-height: none;
    overflow: hidden;
    height: var(--textarea-height);
    &.filled{
      padding: 0;
    }
  }
  &.normal{
    @extend .text-normal;
  }
  &.big{
    @extend .text-big;
  }
  &.title-medium{
    @extend .title-medium;
  }
  &.title-big{
    @extend .title-big;
  }
  &.placeholder-normal{
    &::placeholder{
      @extend .text-normal;
    }
  }
  &.placeholder-big{
    &::placeholder{
      @extend .text-big;
    }
  }
  &.placeholder-title-medium{
    &::placeholder{
      @extend .title-medium;
    }
  }
  &.placeholder-title-big{
    &::placeholder{
      @extend .title-big;
    }
  }
  &.customSize{
    font-size: var(--font-size);
  }
}

.text-value{
  &.normal{
    @extend .text-normal;
  }
  &.big{
    @extend .text-big;
  }
  &.title-medium{
    @extend .title-medium;
  }
  &.title-big{
    @extend .title-big;
  }
  &.program-title{
    color: $blue;
  }
  &.customSize{
    font-size: var(--font-size);
  }
}
.icon {
  height: 16px;
  width: 16px;
}
</style>
